import React from "react"
import { CopyContext, DefaultSectionCopy } from "../CopyProvider"
import { TenantButtonCopy, TenantStatusCopy, TenantStatusCopyType } from "@hydrologiq/gql/graphql"

export function useGetCopyDefault(): DefaultSectionCopy | undefined {
  const context = React.useContext(CopyContext)
  if (context === undefined) {
    throw new Error("useGetCopyDefault must be used within a CopyProvider")
  }
  return context.defaultCopy
}

export function useGetCopyHomeRoute(): { name: string | undefined; path: string | undefined } {
  const context = React.useContext(CopyContext)
  if (context === undefined) {
    return { name: undefined, path: undefined }
  }
  const defaultCopy = context.defaultCopy
  return {
    name: context.copy?.name ?? defaultCopy.name,
    path: context.copy?.base ?? defaultCopy.base,
  }
}

export function useGetCopyButton(name: string): TenantButtonCopy | undefined {
  const context = React.useContext(CopyContext)
  if (context === undefined) return undefined
  return (context.copy?.buttons ?? context.defaultCopy.buttons).find((button) => button.name === name)
}

export function useSafeCopyHeading(identifier: string, fallback: string): string {
  const context = React.useContext(CopyContext)

  if (context === undefined) return fallback

  const headings = context.copy?.headings ?? context.defaultCopy.headings
  const heading = headings.find((h) => h.name === identifier)
  return heading?.value || fallback
}

export function useCopyValues<T extends Record<string, string>>(mappings: T): Record<keyof T, string> {
  const context = React.useContext(CopyContext)

  return Object.keys(mappings).reduce((acc, key) => {
    if (context) {
      const headings = context.copy?.headings ?? context.defaultCopy.headings
      const heading = headings.find((h) => h.name === key)
      acc[key as keyof T] = heading?.value || mappings[key as keyof T]
    } else {
      acc[key as keyof T] = mappings[key as keyof T]
    }
    return acc
  }, {} as Record<keyof T, string>)
}

export function useCopyStatuses(type: TenantStatusCopyType): TenantStatusCopy[] {
  const context = React.useContext(CopyContext)
  const statuses = context?.copy?.statuses ?? context?.defaultCopy.statuses
  if (!statuses) return []
  return statuses.filter((status) => status.type === type)
}
